// src/pages/admin/AdminClubsList.jsx
//
// Founder-only list/search page at /admin/clubs. Lists every club in
// the system with their commercial state, lets Kurt filter by name +
// tier, and links to the detail/edit page. Read-only — no writes happen
// from this page.
//
// See features/admin-club-provisioning/01-requirements.md + 02-wireframe.md.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import TierBadge from '@/components/admin/TierBadge';

const PAGE_SIZE = 100;

const TIER_CHIPS = [
  'free',
  'team',
  'club',
  'growth',
  'elite',
  'enterprise',
  'founding_club',
];

function ageFromNow(ts) {
  if (!ts) return '—';
  const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1)  return 'today';
  if (days < 7)  return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

function trialRelative(ts) {
  if (!ts) return '—';
  const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const daysOut = Math.round((d.getTime() - Date.now()) / 86400000);
  if (daysOut === 0)   return 'today';
  if (daysOut < 0)     return `expired ${Math.abs(daysOut)}d ago`;
  if (daysOut < 30)    return `in ${daysOut}d`;
  if (daysOut < 365)   return `in ${Math.round(daysOut / 30)}mo`;
  return `in ${Math.round(daysOut / 365)}y`;
}

export default function AdminClubsList() {
  const { isFounder } = usePlatformClaims();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState(() => new Set());

  // Defense-in-depth: AdminRoute already gates /admin/* on platform
  // claim, but this page narrows further to founder-only per AC-1 + W-6.
  const blocked = !isFounder;

  async function loadClubs() {
    setLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, 'clubs'),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE),
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setClubs(rows);
    } catch (e) {
      setError(e?.message || 'Failed to load clubs.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (blocked) return;
    loadClubs();
  }, [blocked]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return clubs.filter((c) => {
      if (tierFilter.size > 0) {
        const t = c.tier || null;
        if (!t || !tierFilter.has(t)) return false;
      }
      if (needle) {
        const name = String(c.name || '').toLowerCase();
        if (!name.includes(needle)) return false;
      }
      return true;
    });
  }, [clubs, search, tierFilter]);

  function toggleTier(t) {
    setTierFilter((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  function clearFilters() {
    setSearch('');
    setTierFilter(new Set());
  }

  if (blocked) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">Insufficient privilege.</span>{' '}
            Founder claim required to view club provisioning.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex items-center justify-between mb-4">
        <h1
          className="text-2xl font-bold uppercase tracking-tight text-zinc-900"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          Clubs
        </h1>
        <button
          type="button"
          onClick={loadClubs}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clubs by name…"
          className="w-full rounded-md border border-zinc-200 pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
        />
      </div>

      {/* Tier filter chips */}
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">
          Filter by tier
        </p>
        <div className="flex flex-wrap gap-2">
          {TIER_CHIPS.map((t) => {
            const active = tierFilter.has(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleTier(t)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? 'bg-lime-400 border-lime-500 text-zinc-900'
                    : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                }`}
              >
                {t}
              </button>
            );
          })}
          {(search || tierFilter.size > 0) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-1 rounded-full text-xs font-medium text-zinc-500 hover:text-zinc-800"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-zinc-500 mb-2">
        Showing {filtered.length} of {clubs.length} clubs
        {clubs.length === PAGE_SIZE && ` (capped at ${PAGE_SIZE})`}
      </p>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2 mb-4">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Couldn't load clubs</p>
            <p className="text-red-700">{error}</p>
          </div>
          <button
            type="button"
            onClick={loadClubs}
            className="px-2 py-1 rounded text-xs font-semibold border border-red-300 text-red-800 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !error && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 rounded-md bg-zinc-100 animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Empty states */}
      {!loading && !error && clubs.length === 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-12 text-center text-sm text-zinc-500">
          No clubs in the system yet. New clubs appear here as coaches create them.
        </div>
      )}
      {!loading && !error && clubs.length > 0 && filtered.length === 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-8 text-center text-sm text-zinc-500">
          No clubs match the current filters.{' '}
          <button
            type="button"
            onClick={clearFilters}
            className="text-lime-700 font-semibold hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Table (desktop) / stacked cards (mobile) */}
      {!loading && !error && filtered.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block rounded-2xl border border-zinc-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2">Trial ends</th>
                  <th className="px-4 py-2">Seats</th>
                  <th className="px-4 py-2">Age</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50">
                    <td className="px-4 py-2">
                      <Link
                        to={`/admin/clubs/${c.id}`}
                        className="font-semibold text-zinc-900 hover:underline"
                      >
                        {c.name || <span className="text-zinc-400 italic">(unnamed)</span>}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <TierBadge tier={c.tier} foundingClubNumber={c.foundingClubNumber} />
                    </td>
                    <td className="px-4 py-2 text-zinc-700">{trialRelative(c.trialEndsAt)}</td>
                    <td className="px-4 py-2 text-zinc-700">
                      {c.seatLimit == null ? '—' : c.seatLimit}
                    </td>
                    <td className="px-4 py-2 text-zinc-500">{ageFromNow(c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <div className="sm:hidden space-y-2">
            {filtered.map((c) => (
              <Link
                key={c.id}
                to={`/admin/clubs/${c.id}`}
                className="block rounded-2xl border border-zinc-200 bg-white px-4 py-3 hover:bg-zinc-50"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-zinc-900">
                    {c.name || <span className="text-zinc-400 italic">(unnamed)</span>}
                  </div>
                  <TierBadge tier={c.tier} foundingClubNumber={c.foundingClubNumber} />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-zinc-600">
                  <div>
                    <p className="text-zinc-400 uppercase tracking-wide">Trial</p>
                    <p>{trialRelative(c.trialEndsAt)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 uppercase tracking-wide">Seats</p>
                    <p>{c.seatLimit == null ? '—' : c.seatLimit}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 uppercase tracking-wide">Age</p>
                    <p>{ageFromNow(c.createdAt)}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
